import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Space, message, Upload, Modal, Breadcrumb, Dropdown, MenuProps, UploadProps, Image } from 'antd';
import { PlusOutlined, UploadOutlined, DiffOutlined, DownOutlined, DeleteOutlined, FileOutlined, ArrowLeftOutlined, ExportOutlined, HomeOutlined, LinkOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { uploadFile, deleteFile, exportFiles } from '../../../api/data-project-view';
import { deleteFolder, getFolderAndDocList } from '../../../api/data-project-folder';
import { getCcfileDownloadUrl } from '../../../api/download';
import CreateFolderModal from './CreateFolderModal';
import CreateFileModal from './CreateFileModal';
import CreateTemplateModal from './CreateTemplateModal';
import ExportConfirmModal from './ExportConfirmModal';
import RenameModal from './RenameModal';
import type { ColumnsType } from 'antd/es/table';
import { useSetState } from 'ahooks';
import DocumentFieldSettingModal from '@/pages/documentCompile/onlyofficeFieldSetting';
import { DocumentCompileFileStatusTextMap, DocumentFileType, DocumentSettingType } from '@/pages/documentCompile/type';
import { updateDocumentTemplate } from '../../../api/templateManage';
import useStore from '../../../store/useStore';
import { ToolBarType } from '@/pages/documentCompile/onlyofficeFieldSetting/type';
import { closeDocument } from '@/pages/documentCompile/api/onlyoffice';
import { addDoc, removeDoc } from '@/pages/documentCompile/api/data-project-doc';
import { reviewDataProject } from '@/api/cloudProject';
import { DocStatusEnum } from '@/pages/documentCompile/dict/type';
import { getUrl } from '@/utils/request';
import { getFileMd5 } from '@/utils/tools';
import { formatDate } from '@/utils/date';
import { uploadCCFile } from '@lubanzj/fe-common';
import styles from './index.module.less';
import { ReactComponent as ZlbzFolderIcon } from '@/assets/svg/zlbz-folder.svg'
import { ReactComponent as ZlbzArticleIcon } from '@/assets/svg/zlbz-article.svg'
import { ReactComponent as RelationDocTempIcon } from '@/assets/svg/relation-doc-template.svg'
import LinkFromOtherFolderModal from './LinkFromOtherFolderModal';
import { getUploadFileType, isLegalPreviewFileType, isLegalUploadFileType } from '@/pages/documentCompile/utils/file';
import { renderDocumentStatus } from '@/pages/documentCompile/utils/renderStatus';
import useMethods from '@/pages/documentCompile/onlyofficeFieldSetting/hooks/usemethods';
import { useAuth } from '@/hooks/useAuth';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { confirmAsync } from '@/utils/antd-help';

interface FolderItem {
  folderId: number;
  folderName: string;
  parentId: number | null;
  level: number;
  folderTmplId: number;
  projectId: number;
  viewId: number;
  fileCount: number;
  fileSize: number;
}

enum NewFileType {
  folder = '1',
  article = '2'
}

interface FileItem {
  id: number;
  fileName: string;
  fileType: string;
  fileSize: number;
  createTime: string;
  updateTime: string;
  isFolder?: boolean;
  fileCount?: number;
  type?: DocumentFileType | string;
  docType?: DocumentFileType;
}

interface FolderContentTableProps {
  projectId: string;
  currentFolder: FolderItem;
  onBack: () => void;
  onRefreshParent: () => void;
}

interface BreadcrumbItem {
  folderId: number;
  folderName: string;
  parentId: number | null;
}

const FolderContentTable: React.FC<FolderContentTableProps> = ({
  projectId,
  currentFolder,
  onBack,
  onRefreshParent
}) => {
  // 获取地址参数 
  const [searchParams] = useSearchParams()
  const requestId = searchParams.get('requestId')
  const detailReferrer = searchParams.get('detailReferrer')
  const { hasAuth, codes } = useAuth()
  const authEdit = useMemo(() => hasAuth('630:doc:prep:proj:modify'), [codes])
  const { deatailEditabled, projectDetail, folderList, folderListLoading } = useStore(useShallow(s => ({
    deatailEditabled: s.deatailEditabled,
    projectDetail: s.projectDetail,
    folderList: s.folderList,
    folderListLoading: s.folderListLoading
  })))
  console.log("projectDetail", projectDetail)

  const setStoreState = useStore.setState;
  const [fileList, setFileList] = useState<FileItem[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  // 面包屑导航状态
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<number>(currentFolder.folderId);

  const { handleExport } = useMethods();

  // 使用 useSetState 整理所有状态
  const [state, setState] = useSetState({
    fieldSettingOpen: false,
    curTemp: null,
    newFolderVisible: false,
    newFileVisible: false,
    newTemplateVisible: false,
    exportModalVisible: false,
    imagePreviewVisible: false,
    imagePreviewUrl: '',
    imagePreviewName: '',
    renameModalVisible: false,
    selectedRecord: null
  });

  const [linkModalOpen, setLinkModalOpen] = useState(false);

  const openLinkModal = () => setLinkModalOpen(true);
  const closeLinkModal = () => setLinkModalOpen(false);

  // 创建一个防抖版本的 fetchFileList
  const debouncedFetchFileList = debounce((folderId: number) => {
    fetchFileList(folderId);
  }, 1000);

  // 初始化面包屑
  useEffect(() => {
    setBreadcrumbs([
      {
        folderId: currentFolder.folderId,
        folderName: currentFolder.folderName,
        parentId: currentFolder.parentId
      }
    ]);
    setCurrentFolderId(currentFolder.folderId);
  }, [currentFolder]);

  // 处理点击文件夹或文件
  const handleItemClick = async (record: FileItem) => {
    if (record.isFolder) {
      setStoreState({
        parentFolderId: record.id
      })
      // 点击文件夹，导航到该文件夹
      navigateToFolder(record.id, record.fileName);
    } else {
      // 检查是否为图片类型
      if (record.type === 'jpg' || record.type === 'jpeg' || record.type === 'png' || record.type === 'gif' || record.type === 'bmp' || record.type === 'webp') {
        // 图片类型，进行预览
        try {
          const downloadParams = [{
            fileName: record.fileName,
            fileKey: (record as any).docUrl
          }];

          const data = await getCcfileDownloadUrl(downloadParams);
          const downloadData = data?.[0];

          if (downloadData?.downloadUrls && downloadData.downloadUrls.length > 0) {
            const previewUrl = downloadData.downloadUrls[0];
            // 设置预览状态并显示预览模态框
            setState({
              imagePreviewUrl: previewUrl,
              imagePreviewName: record.fileName,
              imagePreviewVisible: true
            });
          } else {
            message.error('获取图片预览链接失败');
          }
        } catch (error) {
          console.error('获取图片预览链接失败:', error);
          message.error('获取图片预览链接失败');
        }
        return;
      }

      if (!isLegalPreviewFileType(record.fileName)) {
        message.warning('该文件类型不支持预览');
        return;
      }
      // 点击文件，打开文档设置弹窗
      const hackGetFileType = (type: string) => {
        if (type == 'word') {
          return DocumentFileType.WORD
        }
        if (type == 'excel') {
          return DocumentFileType.EXCEL
        }
        if (type.includes('pdf')) {
          return DocumentFileType.PDF
        }
        return type as DocumentFileType
      }
      // const folderList = useStore.getState().folderList

      // if (selectFolder.viewId === currentFolder?.viewId) {
      //   return
      // }
      setStoreState({
        documentType: hackGetFileType(record.type),
        documnetSettingType: DocumentSettingType.DATA_PROJECT_FILE,
        selectedToolBar: ToolBarType.PACKAGE_INFO,
        selectedFileData: record,
        documentSettingProjectData: {
          documentDetail: record,
          type: DocumentSettingType.DATA_PROJECT_FILE,
          projectId: projectId,
          viewId: String(currentFolder?.viewId),
        }
      });
      setState({
        fieldSettingOpen: true,
        curTemp: record
      });
    }
  };

  // 导航到指定文件夹
  const navigateToFolder = (folderId: number, folderName: string) => {
    // 添加到面包屑
    setBreadcrumbs(prev => [...prev, { folderId, folderName, parentId: currentFolderId }]);
    setCurrentFolderId(folderId);
    setStoreState({
      parentFolderId: folderId
    })
    fetchFileList(folderId);
  };

  // 面包屑点击事件
  const handleBreadcrumbClick = (item: BreadcrumbItem, index: number) => {
    if (index === breadcrumbs.length - 1) {
      // 点击当前文件夹，不做任何操作
      return;
    }

    // 截取面包屑到点击的位置
    setBreadcrumbs(prev => prev.slice(0, index + 1));
    setCurrentFolderId(item.folderId);
    fetchFileList(item.folderId);
  };

  // 返回上一级
  const handleBackToParent = () => {
    if (breadcrumbs.length > 1) {
      // 移除最后一个面包屑项
      const newBreadcrumbs = breadcrumbs.slice(0, -1);
      setBreadcrumbs(newBreadcrumbs);
      const parentFolder = newBreadcrumbs[newBreadcrumbs.length - 1];
      setCurrentFolderId(parentFolder.folderId);
      // console.log("parentFolder", parentFolder.folderId)
      setStoreState({
        parentFolderId: parentFolder.folderId
      })
      fetchFileList(parentFolder.folderId);
    } else {
      // 如果只有一个面包屑项，返回到项目列表
      onBack();
    }
  };

  // 获取文件列表（文档和文件夹混合）
  const fetchFileList = async (folderId: number) => {
    try {
      setLoading(true);
      // 清空选中项
      setSelectedRowKeys([]);
      const params = {
        projectId: projectId,
        parentId: folderId,
        pageIndex: 1,
        pageSize: 100
      };
      const result = await getFolderAndDocList(params);

      // 转换数据格式，区分文件夹和文件
      const transformedData = (result || []).map((item: any) => ({
        ...item,
        id: item.id || item.folderId,
        fileName: item.fileName || item.folderName || item.name,
        fileType: item.type || (item.fileName ? 'file' : 'folder'),
        fileSize: item.fileSize || 0,
        createTime: item.createTime,
        updateTime: item.updateTime,
        isFolder: item.folder, // 如果没有fileName，则是文件夹
        fileCount: item.fileCount || 0
      }));

      setFileList(transformedData);
    } catch (error) {
      message.error('获取文件列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentFolder) {
      fetchFileList(currentFolder.folderId);
    }
  }, [currentFolder]);

  // 文件表格列定义
  const fileColumns: ColumnsType<FileItem> = [
    {
      title: '资料目录/名称',
      dataIndex: 'fileName',
      key: 'fileName',
      render: (text: string, record: FileItem) => (
        <Space style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
            {record.isFolder ? (
              <ZlbzFolderIcon />
            ) : (
              <ZlbzArticleIcon />
            )}
          </div>
          <a
            style={{ color: '#1890ff' }}
            onClick={() => handleItemClick(record)}
          >
            {text}
          </a>
        </Space>
      ),
    },
    {
      title: '文件个数',
      dataIndex: 'count',
      key: 'count',
      render: (count: number, record: FileItem) => {
        if (record.isFolder) {
          const displayCount = count || 0;
          return <span style={{ color: displayCount === 0 ? '#ff4d4f' : 'inherit' }}>{displayCount}个</span>;
        }
        return '1个';
      },
    },
    {
      title: '文件大小',
      dataIndex: 'size',
      key: 'size',
      render: (size: number) => {
        if (size < 1024) return `${size}B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
        return `${(size / (1024 * 1024)).toFixed(1)}MB`;
      },
    },
    {
      title: '文件类型',
      dataIndex: 'fileType',
      key: 'fileType',
      render: (type: string, record: FileItem) => record.isFolder ? '文件夹' : '文件',
    },
    {
      title: '文件状态',
      dataIndex: 'signStatus',
      key: 'signStatus',
      align: 'center',
      render: (status: number, record: FileItem) =>record.isFolder ? '' : renderDocumentStatus(status),
    },
    {
      title: '修改日期',
      dataIndex: 'updateTime',
      key: 'updateTime',
      render: (time: string) => formatDate(time, 'YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleRenameFile(record)}
            disabled={!deatailEditabled}
          >
            重命名
          </Button>
          <Button
            type="link"
            onClick={() => handleDeleteFile(record)}
            danger
            disabled={!deatailEditabled}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 重命名文件
  const handleRenameFile = (record) => {
    setState({
      renameModalVisible: true,
      selectedRecord: record
    });
  };

  // 删除文件
  const handleDeleteFile = (record) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除此文件吗？',
      onOk: async () => {
        try {
          if (record.isFolder) {
            await deleteFolder({ folderIds: [record.id], projectId }, requestId)
          } else {
            await removeDoc({
              "folderId": currentFolderId,
              "docIds": [record.id],
              projectId
            }, requestId)
          }
          message.success('删除文件成功');
          fetchFileList(currentFolderId);
        } catch (error) {
          message.error('删除文件失败');
        }
      },
    });
  };

  // 批量删除
  const handleBatchDelete = () => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 项吗？`,
      onOk: async () => {
        try {
          await deleteFile(selectedRowKeys as number[]);
          message.success('删除成功');
          setSelectedRowKeys([]);
          fetchFileList(currentFolderId);
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  // 导出选中项 - 打开确认弹窗
  const handleExportClick = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要导出的项目');
      return;
    }
    setState({ exportModalVisible: true });
  };

  // 确认导出
  const handleExportConfirm = async (convertToPdf: boolean) => {
    const fileMap = new Map(fileList.map(item => [String(item.id), item]));
    const folderIds: React.Key[] = []
    const docIds: React.Key[] = []
    selectedRowKeys.forEach(key => {
      const item = fileMap.get(String(key));
      if (!item) return;
      (item.isFolder ? folderIds : docIds).push(key);
    });
    const parentFolderId = useStore.getState().parentFolderId
    const params = {
      parentId: parentFolderId,
      folderIds,
      convertToPdf,
      docIds,
      projectId: projectId
    }
    try {
      setExportLoading(true);
      await handleExport(params);
      setState({ exportModalVisible: false });
    } catch (error) {
      // 错误已在 useMethods 中处理
      console.error('导出失败:', error);
    } finally {
      setExportLoading(false);
    }
  };

  // 取消导出
  const handleExportCancel = () => {
    setState({ exportModalVisible: false });
  };

  // 刷新当前列表
  const refreshCurrentList = () => {
    fetchFileList(currentFolderId);
  };

  // 审阅项目
  const handleReviewProject = () => {
    Modal.confirm({
      title: '确认操作',
      content: '即将审阅通过，资料状态变为"已完成"！此操作不可撤销，是否确认操作？',
      onOk: async () => {
        try {
          await reviewDataProject({
            projectId: projectId,
          });
          message.success('审阅成功');
          // 刷新项目详情以更新状态
          onRefreshParent();
        } catch (error) {
          message.error('审阅失败');
        }
      },
    });
  };

  const preUploadUrl = getUrl('/cmp-doc/upload/query_upload_url')
  console.log("uploadUrl", preUploadUrl)

  // 上传配置
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    headers: {
      authorization: 'access-token' + localStorage.getItem('token'),
    },
    onChange(info: any) {
      if (info.file.status === 'error') {
        message.error(`${info.file.name} 上传失败`);
      }
    },
    // 不展示上传文件列表
    showUploadList: false,
    customRequest: async (options: any) => {
      try {
        const { file, onSuccess, onError, onProgress } = options;
        console.log("customRequest", file)
        if (!isLegalUploadFileType(file.name)) {
          message.warning('不支持上传无法预览的文件类型');
          return;
        }
        // 1. 计算文件MD5
        const fileMd5 = await getFileMd5(file);

        // 2. 调用预上传接口
        const preUploadParams = [
          {
            fileName: file.name,
            fileMd5: fileMd5,
            fileSize: file.size
          }
        ];

        const preUploadResponse = await fetch(preUploadUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'access-token': 'Bearer ' + localStorage.getItem('token'),
          },
          body: JSON.stringify(preUploadParams)
        });
        if (!preUploadResponse.ok) {
          throw new Error('预上传请求失败');
        }
        const preUploadResult = await preUploadResponse.json();
        const uploadInfo = preUploadResult?.data || {}
        const uploadInfoFirst = uploadInfo?.[0] || {}
        console.log("preUploadResult", preUploadResult)

        if (uploadInfoFirst.proxyFileKey) {
          console.log("file", file)
          addDoc({
            projectId,
            "docName": file.name,
            "folderId": currentFolderId,
            "docUrl": uploadInfoFirst.proxyFileKey,
            "docSize": file.size,
          }, requestId).then(res => {
            console.log("res", res)
            message.success('上传成功')
            debouncedFetchFileList(currentFolderId);
          })
          return
        }
        const handleUploadSuccess = (res: any) => {
          console.log("handleUploadSuccess", res)
          if (res.proxyFileKey) {
            addDoc({
              projectId,
              "docName": file.name,
              "folderId": currentFolderId,
              "docUrl": res.proxyFileKey,
              "docSize": file.size,
            }, requestId).then(res => {
              message.success('上传成功')
              debouncedFetchFileList(currentFolderId);
            })
          } else {
            message.error('上传失败')
            onError(new Error('上传失败'));
          }
        }
        uploadCCFile({ uploadParams: uploadInfoFirst, url: uploadInfoFirst.urls[0], file, onProgress, onSuccess: handleUploadSuccess })
      } catch (error) {
        console.error('上传失败:', error);
        options.onError(error);
      }
    },
  };

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => {
      setSelectedRowKeys(keys);
    },
  };

  const handleFieldSettingOk = (data: any) => {
    console.log(data);
    refreshCurrentList()
  };

  const handleFieldSettingCancel = () => {
    setState({ fieldSettingOpen: false });
    refreshCurrentList()
  };

  const handleFieldSettingSaveSuccess = (data: { fileUrl: string }) => {
    refreshCurrentList();
  };

  const newFolderClick = (v: any) => {
    setState({ newFolderVisible: true })
  }

  const newFileClick = (v: any) => {
    setState({ newFileVisible: true })
  }

  const items: MenuProps['items'] = [
    {
      key: NewFileType.folder,
      label: '新建文件夹',
      onClick: newFolderClick
    },
    {
      key: NewFileType.article,
      label: "新建空文件",
      onClick: newFileClick
    },
  ];

  return (
    <div className={styles.container}>
      {/* 面包屑导航 */}
      {/* <div style={{ marginBottom: '16px', padding: '8px 0' }}>
        <Breadcrumb
          items={breadcrumbs.map((item, index) => ({
            title: (
              <span
                style={{ 
                  cursor: index === breadcrumbs.length - 1 ? 'default' : 'pointer',
                  color: index === breadcrumbs.length - 1 ? '#666' : '#1890ff'
                }}
                onClick={() => handleBreadcrumbClick(item, index)}
              >
                {index === 0 ? (
                  <Space>
                    <HomeOutlined />
                    {item.folderName}
                  </Space>
                ) : (
                  item.folderName
                )}
              </span>
            )
          }))}
        />
      </div> */}

      {/* 页面标题 */}
      <div style={{ marginBottom: '16px', marginLeft: '-4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
          <ZlbzFolderIcon style={{ marginRight: '8px' }} />
          {breadcrumbs[breadcrumbs.length - 1]?.folderName} - 文档列表
        </h3>
        {projectDetail?.docStatus == DocStatusEnum.COMPILING && detailReferrer === 'review' && (
          <Button
            type="primary"
            onClick={handleReviewProject}
          >
            审核通过
          </Button>
        )}
      </div>

      {/* 操作按钮 */}
      <div style={{ marginBottom: '16px', marginLeft: '-9px' }}>
        <Space wrap>
          <Button
            type='link'
            size='small'
            icon={<ArrowLeftOutlined />}
            onClick={handleBackToParent}
          >
            返回
          </Button>

          <Upload {...uploadProps}>
            <Button
              disabled={!deatailEditabled}
              type='link'
              size='small'
              className="c1f1f1f"
              icon={<UploadOutlined />}
            >
              上传
            </Button>
          </Upload>
          <Dropdown placement="bottom" menu={{ items }} >
            <Button
              disabled={!deatailEditabled}
              type='link'
              size='small'
              className="c1f1f1f"
            >
              <Space>
                <PlusOutlined />
                新建
              </Space>
            </Button>
          </Dropdown>

          <Button
            disabled={!deatailEditabled}
            type='link'
            size='small'
            className="c1f1f1f"
            icon={<DiffOutlined style={{ marginTop: '5px' }} />}
            onClick={() => setState({ newTemplateVisible: true })}
          >
            关联文档模板
          </Button>
          <Button
            disabled={!deatailEditabled}
            type='link'
            size='small'
            className="c1f1f1f"
            icon={<LinkOutlined />}
            onClick={openLinkModal}
          >
            引用
          </Button>
          {/* 导出 */}
          <Button
            type='link'
            size='small'
            className="c1f1f1f"
            icon={<ExportOutlined />}
            onClick={handleExportClick}
            loading={exportLoading}
          >
            导出
          </Button>

          {/* <Button
            type='link'
            size='small'
            className="c1f1f1f"
            icon={<ExportOutlined />}
            onClick={handleExport}
            disabled={selectedRowKeys.length === 0}
          >
            导出
          </Button> */}

          {/* <Button
            danger
            icon={<DeleteOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={handleBatchDelete}
          >
            删除
          </Button> */}
        </Space>
      </div>

      {/* 文件表格 */}
      <Table
        rowSelection={rowSelection}
        columns={fileColumns}
        dataSource={fileList}
        loading={loading}
        rowKey="id"
        pagination={false}
        scroll={{ x: 'max-content' }}
        bordered
        size="small"
      />

      {/* 创建文件夹模态框 */}
      <CreateFolderModal
        requestId={requestId}
        visible={state.newFolderVisible}
        onCancel={() => setState({ newFolderVisible: false })}
        onSuccess={() => {
          setState({ newFolderVisible: false });
          refreshCurrentList();
        }}
        projectId={projectId}
        parentFolderId={currentFolderId}
        viewId={currentFolder?.viewId}
      />

      {/* 创建空文件模态框 */}
      <CreateFileModal
        visible={state.newFileVisible}
        onCancel={() => setState({ newFileVisible: false })}
        onSuccess={() => {
          setState({ newFileVisible: false });
          refreshCurrentList();
        }}
        projectId={projectId}
        folderId={currentFolderId}
        viewId={currentFolder?.viewId}
        requestId={requestId}
      />

      {/* 创建模板文件模态框 */}
      <CreateTemplateModal
        requestId={requestId}
        visible={state.newTemplateVisible}
        onCancel={() => setState({ newTemplateVisible: false })}
        onSuccess={() => {
          setState({ newTemplateVisible: false });
          refreshCurrentList();
        }}
        projectId={projectId}
        folderId={currentFolderId}
        viewId={currentFolder?.viewId}
      />

      {/* 文档域设置弹窗 */}
      {
        state.fieldSettingOpen && (
          <DocumentFieldSettingModal
            // settingData={{
            //   type: DocumentSettingType.DATA_PROJECT_FILE,
            //   documentDetail: state.curTemp,
            //   projectId: projectId,
            //   viewId: String(currentFolder?.viewId),
            // }}
            open={state.fieldSettingOpen}
            onOk={handleFieldSettingOk}
            onCancel={handleFieldSettingCancel}
            onSaveSuccess={handleFieldSettingSaveSuccess}
          />
        )
      }

      {/* 导出确认弹窗 */}
      <ExportConfirmModal
        visible={state.exportModalVisible}
        onCancel={handleExportCancel}
        onConfirm={handleExportConfirm}
        loading={exportLoading}
      />

      {/* 图片预览弹窗 */}
      {/* <Modal
        title={`图片预览 - ${state.imagePreviewName}`}
        open={state.imagePreviewVisible}
        onCancel={() => setState({ imagePreviewVisible: false })}
        footer={null}
        width="80%"
        centered
        style={{ maxWidth: '800px' }}
      >
        <Image
          alt={state.imagePreviewName}
          style={{ width: '100%' }}
          src={state.imagePreviewUrl}
          preview={false}
        />
      </Modal> */}
      <Image
        alt={state.imagePreviewName}
        style={{ width: '100%', display: 'none' }}
        src={state.imagePreviewUrl}
        preview={{ visible: state.imagePreviewVisible, onVisibleChange: (visible) => setState({ imagePreviewVisible: visible }) }}
      />

      {/* 重命名弹窗 */}
      {state.renameModalVisible && <RenameModal
        visible={state.renameModalVisible}
        onCancel={() => setState({ renameModalVisible: false, selectedRecord: null })}
        onSuccess={() => {
          setState({ renameModalVisible: false, selectedRecord: null });
          refreshCurrentList();
        }}
        folderId={currentFolderId}
        record={state.selectedRecord}
        requestId={requestId}
      />}

      <LinkFromOtherFolderModal
        open={linkModalOpen}
        onCancel={closeLinkModal}
        onSuccess={() => {
          closeLinkModal();
          fetchFileList(currentFolderId);
        }}
        projectId={projectId}
        targetParentFolderId={currentFolderId}
        excludeFolderId={currentFolder.folderId}
        folderList={folderList}
        folderListLoading={folderListLoading}
        requestId={requestId || ''}
      />


    </div >
  );
};

export default FolderContentTable; 
